import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { rowNavHandlers } from "@/lib/row-nav";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Button } from "@/components/ui/button";
import { Search, Car, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare } from "lucide-react";
import type { Property } from "@shared/schema";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { MessageWholeHouseModal } from "@/components/message-whole-house-modal";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { getGroupFilter, setGroupFilter } from "@/lib/group-filter-state";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { bucketMemberStatus } from "@shared/member-status";
import { useSearchQueryParam } from "@/hooks/use-search-query-param";
import { matchesSearch } from "@/lib/global-search";

const VEHICLE_SORT_FIELDS = ["licensePlate", "licensePlateState", "carMake", "carModel", "carYear", "carColor", "memberName", "propertyName", "memberStatus"] as const;
type SortField = (typeof VEHICLE_SORT_FIELDS)[number];
type SortDir = "asc" | "desc";

interface VehicleRow {
  memberId: number;
  memberName: string;
  memberStatus: string;
  propertyId: number | null;
  propertyName: string;
  licensePlate: string;
  licensePlateState: string;
  carMake: string;
  carModel: string;
  carYear: string;
  carColor: string;
}

export default function AppVehicles() {
  usePageTitle("Vehicles");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [, navigate] = useLocation();
  const [search, setSearch, incomingSearch] = useSearchQueryParam();
  const [groupFilter, setGroupFilterState] = useListState<string>("vehicles:group", () => incomingSearch !== null ? "all" : getGroupFilter("vehicles"), incomingSearch ?? "");
  const [propertyFilter, setPropertyFilterState] = useListState<string>("vehicles:property",
    () => incomingSearch !== null ? "all" : localStorage.getItem("pp:vehicles-property-filter") ?? "all",
    incomingSearch ?? "",
  );
  const setPropertyFilter = (next: string) => {
    localStorage.setItem("pp:vehicles-property-filter", next);
    setPropertyFilterState(next);
  };
  const [sortBy, setSortBy] = useLocalStorageState<SortField>(
    "pp:vehicles-sort-by",
    "memberName",
    (stored) => (VEHICLE_SORT_FIELDS.includes(stored as SortField) ? (stored as SortField) : "memberName"),
  );
  const [sortDir, setSortDir] = useLocalStorageState<SortDir>(
    "pp:vehicles-sort-dir",
    "asc",
    (stored) => (stored === "desc" ? "desc" : "asc"),
  );
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  function handleGroupFilterChange(value: string) {
    setGroupFilterState(value);
    setGroupFilter("vehicles", value);
    setPropertyFilter("all");
  }

  const [statusFilter, setStatusFilterState] = useListState<"active" | "all">("vehicles:status",
    () => (incomingSearch !== null || localStorage.getItem("pp:vehicles-status-filter") === "all" ? "all" : "active"),
    incomingSearch ?? "",
  );
  const setStatusFilter = (next: "active" | "all") => {
    localStorage.setItem("pp:vehicles-status-filter", next);
    setStatusFilterState(next);
  };

  interface VehicleApiRow {
    id: number;
    name: string;
    status: string;
    currentPropertyId: number;
    /** Resolved server-side by id, so it names properties the caller's own
     *  property list does not contain. */
    propertyName: string | null;
    propertyAddress: string | null;
    carMake: string | null;
    carModel: string | null;
    carYear: string | null;
    carColor: string | null;
    licensePlate: string | null;
    licensePlateState: string | null;
  }

  const membersQuery = useQuery<VehicleApiRow[]>({
    queryKey: ["/api/app/vehicles"],
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const properties = propertiesQuery.data || [];

  const groupFilteredProperties = useMemo(() => {
    if (groupFilter === "all") return properties;
    return properties.filter(p => (p.propertyGroup || "") === groupFilter);
  }, [properties, groupFilter]);

  const groupFilteredPropertyIds = useMemo(
    () => new Set(groupFilteredProperties.map(p => p.id)),
    [groupFilteredProperties]
  );

  // Both filters are persisted to localStorage, so a value can outlive the
  // thing it names (property un-shared, renamed group, merged duplicate). A
  // stale value matches nothing and pins the table to empty across reloads,
  // which reads as "there are no vehicles". Drop back to "all" instead.
  useEffect(() => {
    if (propertiesQuery.isLoading || !propertiesQuery.data) return;
    if (propertyFilter !== "all" && !propertiesQuery.data.some((p) => String(p.id) === propertyFilter)) {
      setPropertyFilter("all");
    }
    if (groupFilter !== "all" && !propertiesQuery.data.some((p) => (p.propertyGroup || "") === groupFilter)) {
      handleGroupFilterChange("all");
    }
  }, [propertiesQuery.isLoading, propertiesQuery.data, propertyFilter, groupFilter]);

  function getPropertyName(
    propId: number | null | undefined,
    serverName?: string | null,
    serverAddress?: string | null,
  ): string {
    if (!propId) return "—";
    if (serverName || serverAddress) return serverName || serverAddress || "";
    const prop = properties.find((p) => p.id === propId);
    return prop?.name || prop?.address || "Unknown property";
  }

  const vehicleRows: VehicleRow[] = useMemo(() => {
    const allMembers = membersQuery.data || [];
    return allMembers.map((m) => {
        let carYear = (m.carYear || "").trim();
        let carModel = (m.carModel || "").trim();
        let licensePlate = (m.licensePlate || "").trim();
        let licensePlateState = (m.licensePlateState || "").trim();

        // If year is missing but model starts with a 4-digit year, split it out.
        if (!carYear && carModel) {
          const yearMatch = carModel.match(/^(\d{4})\s+(.+)$/);
          if (yearMatch) {
            carYear = yearMatch[1];
            carModel = yearMatch[2].trim();
          }
        }

        // If state is missing but plate ends with a 2-letter state token, split it out.
        if (!licensePlateState && licensePlate) {
          const stateMatch = licensePlate.match(/^(.+?)[\s\-]+([A-Za-z]{2})$/);
          if (stateMatch) {
            licensePlate = stateMatch[1].trim();
            licensePlateState = stateMatch[2].toUpperCase();
          }
        }

        return {
          memberId: m.id,
          memberName: m.name,
          memberStatus: m.status,
          propertyId: m.currentPropertyId,
          propertyName: getPropertyName(m.currentPropertyId, m.propertyName, m.propertyAddress),
          licensePlate,
          licensePlateState,
          carMake: m.carMake || "",
          carModel,
          carYear,
          carColor: m.carColor || "",
        };
      });
  }, [membersQuery.data, properties]);

  function toggleSort(field: SortField) {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  }

  const filtered = useMemo(() => {
    let rows = vehicleRows;

    // Status filter: only skip when search is active (so typing finds moved-out members too).
    // Bucket rather than compare the raw string — PadSplit sends "Current",
    // "In Residence" and friends, so an exact `=== "active"` test silently
    // hides live members.
    if (statusFilter === "active" && !search.trim()) {
      rows = rows.filter((r) => bucketMemberStatus(r.memberStatus) === "active");
    }

    if (groupFilter !== "all") {
      rows = rows.filter((r) => r.propertyId !== null && groupFilteredPropertyIds.has(r.propertyId as number));
    }

    if (propertyFilter !== "all") {
      rows = rows.filter((r) => String(r.propertyId) === propertyFilter);
    }

    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rows = rows.filter((r) => matchesSearch(s, [
        r.licensePlate, r.licensePlateState, r.carMake, r.carModel,
        r.carYear, r.carColor, r.memberName, "vehicle vehicles car cars auto",
      ]));
    }

    rows = [...rows].sort((a, b) => {
      const aVal = (a[sortBy] || "").toLowerCase();
      const bVal = (b[sortBy] || "").toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [vehicleRows, search, statusFilter, propertyFilter, groupFilter, groupFilteredPropertyIds, sortBy, sortDir]);

  function SortIcon({ field }: { field: SortField }) {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  }

  function ColHeader({ field, label }: { field: SortField; label: string }) {
    return (
      <button
        className="flex items-center gap-1 hover:text-gray-800"
        onClick={() => toggleSort(field)}
        data-testid={`sort-${field}`}
      >
        {label} <SortIcon field={field} />
      </button>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="maintenance" />
        <MessageWholeHouseModal open={broadcastOpen} onOpenChange={setBroadcastOpen} />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-vehicles-title" data-tour="vehicles-heading">Vehicles</h1>
            <p className="text-sm text-gray-500 mt-1">Look up any vehicle across all members and properties.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBroadcastOpen(true)}
              data-testid="button-vehicles-message-whole-house"
            >
              <MessageSquare className="h-4 w-4 mr-1.5" />
              Message whole house
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="vehicles" />
              <GuideButton pageKey="vehicles" />
            </div>
            <span data-tour="vehicles-add" />
          </div>
          <GuideNudgeBanner pageKey="vehicles" />
        </div>

        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <PropertyGroupFilter
              properties={properties}
              value={groupFilter}
              onChange={handleGroupFilterChange}
            />
            <SearchableSelect
              value={propertyFilter}
              onValueChange={setPropertyFilter}
              placeholder="All Properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties match."
              testId="select-vehicles-property-filter"
              className="w-[200px] h-9"
              options={[
                { value: "all", label: "All Properties" },
                ...groupFilteredProperties.map((p) => ({
                  value: String(p.id),
                  label: p.name || p.address || `Property #${p.id}`,
                  keywords: `${p.name ?? ""} ${p.address ?? ""}`,
                })),
              ]}
            />
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "active" | "all")}>
              <SelectTrigger className="w-[140px] h-9" data-testid="select-vehicles-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by plate, make, model, color..."
              className="pl-9 w-[300px] h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-vehicles-search"
            />
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-tour="vehicles-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="licensePlate" label="License Plate" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="licensePlateState" label="State" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="carMake" label="Make" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="carModel" label="Model" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="carYear" label="Year" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="carColor" label="Color" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="memberName" label="Member" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="propertyName" label="Property" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <ColHeader field="memberStatus" label="Status" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {membersQuery.isLoading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <Car className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">
                      {vehicleRows.length === 0
                        ? "No vehicle records found. Add vehicle information to member profiles to see them here."
                        : "No vehicles match your filters."}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr
                    key={row.memberId}
                    className="hover:bg-gray-50 cursor-pointer"
                    {...rowNavHandlers(`/app/members/${row.memberId}`, navigate)}
                    data-testid={`row-vehicle-${row.memberId}`}
                  >
                    <td className="px-4 py-3 font-mono text-sm font-medium text-gray-900" data-testid={`text-vehicle-plate-${row.memberId}`}>
                      <Link href={`/app/members/${row.memberId}`} className="block -mx-4 -my-3 px-4 py-3" onClick={(e) => e.stopPropagation()}>{row.licensePlate || "—"}</Link>
                    </td>
                    <td className="px-4 py-3 text-gray-600" data-testid={`text-vehicle-state-${row.memberId}`}>
                      {row.licensePlateState || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700" data-testid={`text-vehicle-make-${row.memberId}`}>
                      {row.carMake || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-700" data-testid={`text-vehicle-model-${row.memberId}`}>
                      {row.carModel || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600" data-testid={`text-vehicle-year-${row.memberId}`}>
                      {row.carYear || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600" data-testid={`text-vehicle-color-${row.memberId}`}>
                      {row.carColor || "—"}
                    </td>
                    <td className="px-4 py-3 font-medium text-brand hover:underline" data-testid={`text-vehicle-member-${row.memberId}`}>
                      {row.memberName}
                    </td>
                    <td className="px-4 py-3 text-gray-600" data-testid={`text-vehicle-property-${row.memberId}`}>
                      {row.propertyName}
                    </td>
                    <td className="px-4 py-3" data-testid={`text-vehicle-status-${row.memberId}`}>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        row.memberStatus === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}>
                        {row.memberStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/app/messages?memberId=${row.memberId}`);
                        }}
                        data-testid={`button-vehicle-message-${row.memberId}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5 mr-1" />
                        Message
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!membersQuery.isLoading && filtered.length > 0 && (
          <p className="mt-2 text-xs text-gray-400" data-testid="text-vehicles-count">
            {filtered.length} vehicle{filtered.length !== 1 ? "s" : ""} found
          </p>
        )}
      </div>
    </AppLayout>
  );
}
